import {
  useEffect, useState,
} from 'react'
import dynamic from 'next/dynamic'

import { useIsExtendScriptLoaded } from '@/hooks/useIsExtendScriptLoaded'
import { useProduct } from '@/hooks/useProduct'
import useAdjustArrivalDate from '@/hooks/useArrivalDateExperiment'
import { error } from '@/services/Log'
import { ExtendWarranty } from '@/services/ExtendWarranty/index'
import { ENABLE_EXTEND } from '@/services/Configuration'
import { truthy } from '@/helpers/truthy'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'

import Row from '@csc/dls/Row'
import Col from '@csc/dls/Col'
import Text from '@csc/dls/Text'
import Link from '@csc/dls/Link'
import Heading3 from '@csc/dls/Heading3'
import ApplePayButton from '@/components/ApplePay/ApplePayButton'
import { StandardCart } from '@/types/ShopFront/CheckoutStandards'
import useCanUseApplePay from '@/hooks/useCanUseApplePay'
import usePDPApplePayCheckout from '@/components/ApplePay/usePDPApplePayCheckout'
import PrimaryButton from '@csc/dls/PrimaryButton'
import Quantity from '@csc/dls/Quantity'
import PaypalCtaWithLogic from '@/components/PaypalCTA/PaypalCtaWithLogic'
import usePDPCheckout from '@/components/Checkout/usePDPCheckout'
import { bloomreachAddToCartEvent } from '@/components/ScriptIntegrations/BloomreachEvents'
import { isFeatureEnabled } from '@/helpers/isFeatureEnabled'
import { useGetExperiment } from '../../../helpers/getVWOExperiment'
import useMobileDetect from '../../../hooks/useMobileDetect'
import LoadingModal from '../../Account/Payment/CardsModal/LoadingModal'
import { usePaymentStatus } from '../../../hooks/usePaymentStatus'

const EstimatedArrival = dynamic(import('./EstimatedArrival'))

const shouldShowApplePayOnPDP = () => (
  isFeatureEnabled('pdpApplePayCtaEnabled')
  && isFeatureEnabled('applePayEnabled')
)

const shouldShowPayPalOnPDP = () => (
  isFeatureEnabled('pdpPayPalCtaEnabled')
  && isFeatureEnabled('payPalEnabled')
)

export const OUT_OF_STOCK_MESSAGE = 'Out of stock'

const AddProductToCartForm: React.FC<{
  setCart: (newCart: StandardCart | null) => void
}> = ({
  setCart,
}) => {
  const { paymentStatus, dispatchPaymentStatus, helpMessage } = usePaymentStatus()
  const [isLoading, setIsLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [quantityError, setQuantityError] = useState(false)

  const {
    product,
    sku,
    warrantyVariant,
    addToCart,
    inStock,
    selectedVariant,
    user,
    inventoryLevel,
    isBackOrder,
  } = useProduct()
  const { canUseApplePay, paymentRequest: paymentRequestApple } = useCanUseApplePay({
    price: selectedVariant?.sale_price,
    paymentMethod: 'applePay',
  })
  const { canUseApplePay: canUseLink, paymentRequest: paymentRequestLink } = useCanUseApplePay({
    price: selectedVariant?.sale_price,
    paymentMethod: 'link',
  })
  const extendScriptLoaded = useIsExtendScriptLoaded()
  const shouldAdjustArrivalDate = useAdjustArrivalDate()

  const handleAddToCart = (shouldOfferExtendModal: boolean) => async (event?: {
    preventDefault?: () => void,
    stopPropagation?: () => void,
  }) => {
    let success = false
    let newCart: StandardCart | null = null
    try {
      setIsLoading(true)
      event?.preventDefault?.()
      event?.stopPropagation?.()
      setQuantityError(!quantity)
      if (!quantity) {
        return {
          success: false,
          newCart: null,
        }
      }
      if (product && selectedVariant) {
        const { name, category_info: category } = product
        const result = await addToCart(shouldOfferExtendModal)(quantity)
        const [{ default: trackAddToCart }] = await allPromisesWithRetries(() => [import('@/services/HeapAnalytics/trackAddToCart')])
        const [{ addToCartConnexityEvent }] = await allPromisesWithRetries(() => [import('@/helpers/ConnexityEvents')])

        if (result.success && result.newCart) {
          success = true
          newCart = result.newCart
        }
        if (result.success) {
          allPromisesWithRetries(() => [import('@/helpers/GAEvents')])
            .then(([{ productGA }]) => productGA({
              name,
              sku,
              price: `${selectedVariant.price || product.price}`,
              salePrice: selectedVariant.calculated_price,
              variantId: selectedVariant.id,
              categories: category,
              event: 'addToCart',
              brand: product.brand,
              quantity,
              customerId: user?.entityId,
              customerEmail: user?.email,
              cartTotal: result?.newCart?.total || 0,
            }))
            .catch(error)
          try {
            trackAddToCart({
              name,
              sku,
              price: selectedVariant.price || product.price,
              salePrice: selectedVariant.calculated_price,
              variantId: selectedVariant.id,
              categories: category,
              brand: product.brand,
              quantity,
              customerId: user?.entityId,
              customerEmail: user?.email,
              cartTotal: result?.newCart?.total || 0,
              backOrder: isBackOrder,
            })
          } catch (err) {
            error('Failed to log data in heap', err)
          }
          addToCartConnexityEvent(product)
          bloomreachAddToCartEvent(selectedVariant.sku)
        }
      }

      window?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      document.getElementsByTagName?.('body')[0]?.classList?.add?.('has-cart')
    } catch (err) {
      error('Failed to add to cart', err)
    } finally {
      setIsLoading(false)
    }
    return {
      success,
      newCart,
    }
  }

  useEffect(() => {
    if (!extendScriptLoaded || !sku) {
      return
    }
    // TODO: Kill this and move Extend offers to SSR
    ExtendWarranty.initXPDP(sku)
  }, [sku, extendScriptLoaded])

  const quantityOptions = (
    (new Array(10).fill(0))
      .map((a, b) => b + 1)
      .filter((i) => !selectedVariant || inventoryLevel >= i)
  )

  const quantityOverflow = selectedVariant && inventoryLevel < quantity
  const showNonReturnable = !truthy(warrantyVariant?.is_returnable)

  const {
    onStartCheckout,
    modalContent,
    onCloseModal,
    ctaRef,
  } = usePDPApplePayCheckout({
    paymentRequest: paymentRequestApple,
    canUseApplePay,
    addProductFunction: handleAddToCart(false),
    setCart,
    walletName: 'applePay',
    dispatchPaymentStatus,
  })
  const {
    onStartCheckout: onStartCheckoutLink,
    modalContent: modalContentLink,
    onCloseModal: onCloseModalLink,
    ctaRef: ctaRefLink,
  } = usePDPApplePayCheckout({
    paymentRequest: paymentRequestLink,
    canUseApplePay: canUseLink,
    addProductFunction: handleAddToCart(false),
    setCart,
    walletName: 'link',
    dispatchPaymentStatus,
  })

  const {
    onStartCheckout: onPaypalCheckoutStart,
    beforeAbort: recreateCart,
  } = usePDPCheckout({
    setCart,
    addProductFunction: handleAddToCart(false),
  })
  // TODO: Remove the useEffect and useState when the feature is fully rolled out
  // This might drop PDP CLS scores
  const isPayPalVisible = shouldShowPayPalOnPDP
  const [isApplePayVisible, setIsApplePayVisible] = useState(false)
  useEffect(() => {
    setIsApplePayVisible(shouldShowApplePayOnPDP() && canUseApplePay)
  }, [canUseApplePay])
  const isLinkExperimentOn = useGetExperiment('linkPay')

  const isMobile = useMobileDetect()
  const [paymentOptionsExpanded, setPaymentOptionExpanded] = useState(false)
  const renderPaymentButtons = () => {
    if (isMobile && isApplePayVisible) {
      return (
        <Col span={12}>
          <ApplePayButton
            applePayExperience="pdp"
            onStartCheckout={onStartCheckout}
            modalContent={modalContent}
            onCloseModal={onCloseModal}
            ctaRef={ctaRef}
            paymentMethod="applePay"
          />
          {!paymentOptionsExpanded && (
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={() => {
                setPaymentOptionExpanded((prev) => !prev)
              }}
            >
              <Text bold size="md" className="underline">More payment options</Text>
            </button>
          </div>
          )}
          {paymentOptionsExpanded && (
            <Row>
              <Col span={6}>
                <PaypalCtaWithLogic
                  forceReRender={[quantity]}
                  onCancel={recreateCart}
                  cartGenerator={onPaypalCheckoutStart}
                  recreateCart={recreateCart}
                />
              </Col>
              <Col span={6}>
                <ApplePayButton
                  onStartCheckout={onStartCheckoutLink}
                  modalContent={modalContentLink}
                  onCloseModal={onCloseModalLink}
                  ctaRef={ctaRefLink}
                  paymentMethod="link"
                />
              </Col>
            </Row>
          )}
        </Col>
      )
    }
    const buttons = [
      isApplePayVisible ? (
        <ApplePayButton
          applePayExperience="pdp"
          onStartCheckout={onStartCheckout}
          modalContent={modalContent}
          onCloseModal={onCloseModal}
          ctaRef={ctaRef}
          paymentMethod="applePay"
        />
      ) : null,
      isPayPalVisible() ? (
        <PaypalCtaWithLogic
          forceReRender={[quantity]}
          onCancel={recreateCart}
          cartGenerator={onPaypalCheckoutStart}
          recreateCart={recreateCart}
        />
      ) : null,
      isFeatureEnabled('isLinkEnabled') && isLinkExperimentOn ? (
        <ApplePayButton
          onStartCheckout={onStartCheckoutLink}
          modalContent={modalContentLink}
          onCloseModal={onCloseModalLink}
          ctaRef={ctaRefLink}
          paymentMethod="link"
        />
      ) : null,
    ]
    return (
      <Col span={12} data-testid="hi">
        <div className="flex justify-between gap-2">
          {buttons}
        </div>
      </Col>
    )
  }
  if (!product) {
    return null
  }
  return (
    <form
      id="add-to-cart-form"
      onSubmit={handleAddToCart(true)}
      onChange={() => { }}
    >
      <Row data-testid="submit-container">
        { // TODO: Check if Extend is available before adding the Div
          ENABLE_EXTEND && (
            <Col span={12}>
              <div id="extend-offer" data-testid="PDP_extend" />
            </Col>
          )
        }
        <Col span={12}>
          <Row gapY="sm">
            <Col span={!isApplePayVisible && !isPayPalVisible() ? 12 : 3}>
              <div className="h-full flex flex-row items-end">
                <Heading3 textTag="p">QTY:</Heading3>
              </div>
            </Col>
            {/* eslint-disable */}
            {!isLinkExperimentOn ? (isApplePayVisible ? (
              <>
                <Col span={9}>
                  <ApplePayButton
                    applePayExperience="pdp"
                    onStartCheckout={onStartCheckout}
                    modalContent={modalContent}
                    onCloseModal={onCloseModal}
                    ctaRef={ctaRef}
                    paymentMethod="applePay"
                  />
                </Col>
              </>
            ) : (isPayPalVisible() && (
              <Col className="h-12 z-10" span={9}>
                <PaypalCtaWithLogic
                  forceReRender={[quantity]}
                  onCancel={recreateCart}
                  cartGenerator={onPaypalCheckoutStart}
                  recreateCart={recreateCart}
                />
              </Col>
            )) || null) : <Col span={9} />}
            {/* eslint-enable */}
            <Col span={3}>
              <Quantity
                hideLabel
                disabled={!inStock}
                id="PDP_Quantity_Select"
                name="qty"
                label="qty"
                className=""
                quantityWidth="w-full"
                data-quantity-change
                value={!inStock ? 0 : quantity}
                onChange={setQuantity}
                options={quantityOptions}
              />
            </Col>
            <Col span={9}>
              <Row cols={2}>
                {/* eslint-disable */}
                {!isLinkExperimentOn ? (isApplePayVisible && isPayPalVisible() ? (
                  <Col className="h-12 z-10 max-w-full" span={{ sm: 2, md: 1 }}>
                    <PaypalCtaWithLogic
                      forceReRender={[quantity]}
                      onCancel={recreateCart}
                      recreateCart={recreateCart}
                      cartGenerator={onPaypalCheckoutStart}
                    />
                  </Col>
                ) : null) : null}
                <Col span={isLinkExperimentOn ? 2 : (isApplePayVisible && isPayPalVisible() ? { sm: 2, md: 1 } : 2)}>
                {/* eslint-enable */}
                  <PrimaryButton
                    id="form-action-addToCart"
                    data-button-type="add-cart"
                    htmlType="submit"
                    type={quantityOverflow || !inStock ? 'disabled' : 'primary'}
                    disabled={quantityOverflow || !inStock}
                    className="relative max-h-12"
                    data-testid="product-action-addToCart"
                  >
                    {isLoading
                      ? '... Adding to cart'
                      : 'ADD TO CART'}
                  </PrimaryButton>
                </Col>
              </Row>
              {!isLinkExperimentOn && (
              <Row cols={6}>
                <Col span={6}>
                  {isFeatureEnabled('isLinkEnabled') && isLinkExperimentOn && (
                  <ApplePayButton
                    applePayExperience="pdp"
                    onStartCheckout={onStartCheckoutLink}
                    modalContent={modalContentLink}
                    onCloseModal={onCloseModalLink}
                    ctaRef={ctaRefLink}
                    paymentMethod="link"
                  />
                  )}
                </Col>
              </Row>
              )}
            </Col>
            {isLinkExperimentOn && renderPaymentButtons()}
            {(quantityOverflow && inStock) ? (
              <Col span={12}>
                <Text size="sm" bold className="text-error w-full text-left">
                  Limited Quantity -
                  {inventoryLevel}
                  {' '}
                  left
                </Text>
              </Col>
            ) : (
              inStock && (
                <>
                  {!isBackOrder && (
                    <Col span={4} />
                  )}
                  <Col span={isBackOrder ? 12 : 8} id="EstimatedArrival">
                    <EstimatedArrival
                      variant={warrantyVariant}
                      finalDateOffset={shouldAdjustArrivalDate ? 5 : 0}
                    />
                  </Col>
                </>
              )
            )}
          </Row>
        </Col>
        {showNonReturnable && (
          <Col span={12} className="py-2 md:py-1 px-2 text-center" style={{ backgroundColor: '#FBFBEF' }}>
            <div className="w-full flex-row items-center">
              <Text className="inline-block" size="sm">
                This item is non-returnable.
                {' '}
                See our
              </Text>
              <Text size="sm" className="inline-block mx-0.5">
                <Link
                  target="_blank"
                  href="/faq/#returns-exchange"
                  className="inline-block"
                  aria-label="Return Policy Page"
                >
                  Return Policy
                </Link>
              </Text>
              <Text className="inline-block" size="sm">
                for details.
              </Text>
            </div>
          </Col>
        )}

      </Row>
      {quantityError && <p className="out-of-stock-alert">Please enter valid quantity</p>}
      {quantityError && (
        <Text className="text-error" id="pdp-productstock-modal">
          Sorry, we do not have enough stock on hand for the
          {'quantity you\'ve selected. Please try again.'}
        </Text>
      )}
      <LoadingModal
        open={paymentStatus !== 'idle'}
        message={helpMessage}
        closeable
        loading={paymentStatus === 'in progress'}
        onClose={() => {
          dispatchPaymentStatus({
            type: 'reset',
            error: null,
          })
        }}
      />
    </form>
  )
}

export default AddProductToCartForm
